import "../../../src/components/VItemGroup/VItemGroup.sass";
import Comparable from '../../mixins/comparable';
import Proxyable from '../../mixins/proxyable';
import Themeable from '../../mixins/themeable';
import mixins from '../../util/mixins';
import { consoleWarn } from '../../util/console';
export const BaseItemGroup = mixins(Comparable, Proxyable, Themeable).extend({
    name: 'base-item-group',
    props: {
        activeClass: {
            type: String,
            default: 'v-item--active'
        },
        mandatory: Boolean,
        max: {
            type: [Number, String],
            default: null
        },
        multiple: Boolean,
        tag: {
            type: String,
            default: 'div'
        }
    },
    data() {
        return {
            internalLazyValue: this.value !== undefined ? this.value : this.multiple ? [] : undefined,
            items: []
        };
    },
    computed: {
        classes() {
            return {
                'v-item-group': true,
                ...this.themeClasses
            };
        },
        selectedIndex() {
            return this.selectedItem && this.items.indexOf(this.selectedItem) || -1;
        },
        selectedItem() {
            if (this.multiple)
                return undefined;
            return this.selectedItems[0];
        },
        selectedItems() {
            return this.items.filter((item, index) => {
                return this.toggleMethod(this.getValue(item, index));
            });
        },
        selectedValues() {
            if (this.internalValue == null)
                return [];
            return Array.isArray(this.internalValue) ? this.internalValue : [this.internalValue];
        },
        toggleMethod() {
            if (!this.multiple) {
                return v => this.valueComparator(this.internalValue, v);
            }
            const internalValue = this.internalValue;
            if (Array.isArray(internalValue)) {
                return v => internalValue.some(intern => this.valueComparator(intern, v));
            }
            return () => false;
        }
    },
    watch: {
        internalValue: 'updateItemsState',
        items: 'updateItemsState'
    },
    created() {
        if (this.multiple && !Array.isArray(this.internalValue)) {
            consoleWarn('Model must be bound to an array if the multiple property is true.', this);
        }
    },
    methods: {
        genData() {
            return {
                class: this.classes
            };
        },
        getValue(item, i) {
            return item.value === undefined ? i : item.value;
        },
        onClick(item) {
            this.updateInternalValue(this.getValue(item, this.items.indexOf(item)));
        },
        register(item) {
            const index = this.items.push(item) - 1;
            item.$on('change', () => this.onClick(item));
            if (this.mandatory && !this.selectedValues.length) {
                this.updateMandatory();
            }
            this.updateItem(item, index);
        },
        unregister(item) {
            if (this._isDestroyed)
                return;
            const index = this.items.indexOf(item);
            const value = this.getValue(item, index);
            this.items.splice(index, 1);
            const valueIndex = this.selectedValues.indexOf(value);
            if (valueIndex < 0)
                return;
            if (!this.mandatory) {
                return this.updateInternalValue(value);
            }
            if (this.multiple && Array.isArray(this.internalValue)) {
                this.internalValue = this.internalValue.filter(v => v !== value);
            }
            else {
                this.internalValue = undefined;
            }
            if (!this.selectedItems.length) {
                this.updateMandatory(true);
            }
        },
        updateItem(item, index) {
            const value = this.getValue(item, index);
            item.isActive = this.toggleMethod(value);
        },
        updateItemsState() {
            this.$nextTick(() => {
                if (this.mandatory && !this.selectedItems.length) {
                    return this.updateMandatory();
                }
                this.items.forEach(this.updateItem);
            });
        },
        updateInternalValue(value) {
            this.multiple ? this.updateMultiple(value) : this.updateSingle(value);
        },
        updateMandatory(last) {
            if (!this.items.length)
                return;
            const items = this.items.slice();
            if (last)
                items.reverse();
            const item = items.find(item => !item.disabled);
            if (!item)
                return;
            const index = this.items.indexOf(item);
            this.updateInternalValue(this.getValue(item, index));
        },
        updateMultiple(value) {
            const defaultValue = Array.isArray(this.internalValue) ? this.internalValue : [];
            const internalValue = defaultValue.slice();
            const index = internalValue.findIndex(val => val === value);
            if (this.mandatory &&
                index > -1 &&
                internalValue.length - 1 < 1)
                return;
            if (this.max != null &&
                index < 0 &&
                internalValue.length + 1 > this.max)
                return;
            index > -1 ? internalValue.splice(index, 1) : internalValue.push(value);
            this.internalValue = internalValue;
        },
        updateSingle(value) {
            const isSame = value === this.internalValue;
            if (this.mandatory && isSame)
                return;
            this.internalValue = isSame ? undefined : value;
        }
    },
    render(h) {
        return h(this.tag, this.genData(), this.$slots.default);
    }
});
export default BaseItemGroup.extend({
    name: 'v-item-group',
    provide() {
        return {
            itemGroup: this
        };
    }
});
